import { assertCurrencyCode, currencyExponent } from './currency-exponents.js';
export const MAX_SAFE_MINOR_AMOUNT = Number.MAX_SAFE_INTEGER;
const DECIMAL_PATTERN = /^([+-])?(\d+)(?:\.(\d+))?$/;
const NON_BREAKING_SPACE = '\u00A0';
const THOUSANDS_BOUNDARY_PATTERN = /\B(?=(\d{3})+(?!\d))/g;
const TRAILING_ZEROS_PATTERN = /\.?0+$/;
export const assertSafeMinorAmount = (value, label = 'amountMinor') => {
    if (!Number.isSafeInteger(value)) {
        throw new RangeError(`${label} must be a safe integer, received ${String(value)}`);
    }
    return value;
};
export const toSafeMinorAmount = (value, label = 'amountMinor') => {
    const asNumber = Number(value);
    if (!Number.isSafeInteger(asNumber)) {
        throw new RangeError(`${label} does not fit a safe integer, received ${value.toString()}`);
    }
    return asNumber;
};
export const parseDecimal = (value) => {
    const matched = DECIMAL_PATTERN.exec(value.trim());
    if (matched === null) {
        throw new RangeError(`Expected a plain decimal string, received "${value}"`);
    }
    const [, sign, whole, fraction] = matched;
    if (whole === undefined) {
        throw new RangeError(`Expected a plain decimal string, received "${value}"`);
    }
    const digits = BigInt(`${whole}${fraction ?? ''}`);
    return {
        units: sign === '-' ? -digits : digits,
        scale: fraction?.length ?? 0,
    };
};
export const formatDecimal = (decimal) => {
    if (!Number.isInteger(decimal.scale) || decimal.scale < 0) {
        throw new RangeError(`scale must be a non-negative integer, received ${String(decimal.scale)}`);
    }
    const isNegative = decimal.units < 0n;
    const digits = (isNegative ? -decimal.units : decimal.units)
        .toString()
        .padStart(decimal.scale + 1, '0');
    const whole = digits.slice(0, digits.length - decimal.scale);
    const fraction = decimal.scale === 0 ? '' : `.${digits.slice(digits.length - decimal.scale)}`;
    return `${isNegative ? '-' : ''}${whole}${fraction}`;
};
/**
 * A positive decimal string built from a `number`, for the one case that cannot be avoided: a feed
 * that publishes rates as JSON numbers while design note C0 keeps every rate a string. Nothing else
 * should reach for this - money is never built from a `number`, and a rate that arrived as a string
 * stays the string it arrived as.
 *
 * `String(value)` will not do. Below 1e-6 JavaScript switches to exponent notation and above 1e21
 * it does the same, and `parseDecimal` refuses both by construction - so the result would be a rate
 * the rest of this package cannot read.
 *
 * The two checks at the end are what make the answer trustworthy. The pattern says the result is a
 * string `parseDecimal` accepts; the round trip says the digits parse back to the very same number.
 * A feed that starts publishing more decimals than `maxScale` therefore fails loudly instead of
 * being quietly rounded into a column.
 */
export const decimalStringOf = (value, maxScale) => {
    if (!Number.isInteger(maxScale) || maxScale < 0) {
        throw new RangeError(`maxScale must be a non-negative integer, received ${String(maxScale)}`);
    }
    if (!Number.isFinite(value) || value <= 0) {
        throw new RangeError(`Expected a positive finite number, received ${String(value)}`);
    }
    const padded = value.toFixed(maxScale);
    const trimmed = maxScale === 0 ? padded : padded.replace(TRAILING_ZEROS_PATTERN, '');
    if (!DECIMAL_PATTERN.test(trimmed) || Number(trimmed) !== value) {
        throw new RangeError(`${String(value)} has no exact decimal form within ${maxScale} places`);
    }
    return trimmed;
};
export const divideRoundHalfAwayFromZero = (numerator, denominator) => {
    if (denominator === 0n) {
        throw new RangeError('Cannot divide by zero');
    }
    const isNegativeResult = numerator < 0n !== denominator < 0n;
    const absoluteNumerator = numerator < 0n ? -numerator : numerator;
    const absoluteDenominator = denominator < 0n ? -denominator : denominator;
    const quotient = absoluteNumerator / absoluteDenominator;
    const remainder = absoluteNumerator % absoluteDenominator;
    const roundsAway = remainder * 2n >= absoluteDenominator;
    const magnitude = roundsAway ? quotient + 1n : quotient;
    return isNegativeResult ? -magnitude : magnitude;
};
export const createMoney = (amountMinor, currency) => ({
    amountMinor: assertSafeMinorAmount(amountMinor),
    currency: assertCurrencyCode(currency),
});
const assertSameCurrency = (left, right) => {
    if (assertCurrencyCode(left.currency) !== assertCurrencyCode(right.currency)) {
        throw new TypeError(`Cannot mix ${left.currency} and ${right.currency} in one amount`);
    }
    return left.currency;
};
export const addMoney = (left, right) => {
    const currency = assertSameCurrency(left, right);
    const total = assertSafeMinorAmount(left.amountMinor) + assertSafeMinorAmount(right.amountMinor);
    return { amountMinor: assertSafeMinorAmount(total, 'sum'), currency };
};
export const sumMoney = (values, currency) => values.reduce(addMoney, createMoney(0, currency));
export const multiplyMoney = (value, factor) => {
    if (!Number.isInteger(factor)) {
        throw new RangeError(`factor must be an integer, received ${String(factor)}`);
    }
    const product = assertSafeMinorAmount(value.amountMinor) * factor;
    return {
        amountMinor: assertSafeMinorAmount(product, 'product'),
        currency: assertCurrencyCode(value.currency),
    };
};
export const toMinorUnits = (value, currency) => {
    const exponent = currencyExponent(currency);
    const decimal = parseDecimal(value);
    const numerator = decimal.units * 10n ** BigInt(exponent);
    const denominator = 10n ** BigInt(decimal.scale);
    return toSafeMinorAmount(divideRoundHalfAwayFromZero(numerator, denominator));
};
export const fromMinorUnits = (amountMinor, currency) => formatDecimal({
    units: BigInt(assertSafeMinorAmount(amountMinor)),
    scale: currencyExponent(currency),
});
const groupDigits = (digits, separator) => digits.replace(THOUSANDS_BOUNDARY_PATTERN, separator);
export const formatMoney = (value, options = {}) => {
    const groupSeparator = options.groupSeparator ?? NON_BREAKING_SPACE;
    const decimalSeparator = options.decimalSeparator ?? ',';
    const symbol = options.symbol ?? assertCurrencyCode(value.currency);
    const symbolPosition = options.symbolPosition ?? 'after';
    const isNegative = assertSafeMinorAmount(value.amountMinor) < 0;
    const plain = fromMinorUnits(Math.abs(value.amountMinor), value.currency);
    const [whole = '0', fraction] = plain.split('.');
    const grouped = groupDigits(whole, groupSeparator);
    const withFraction = fraction === undefined ? grouped : `${grouped}${decimalSeparator}${fraction}`;
    const signed = isNegative ? `-${withFraction}` : withFraction;
    return symbolPosition === 'before'
        ? `${symbol}${NON_BREAKING_SPACE}${signed}`
        : `${signed}${NON_BREAKING_SPACE}${symbol}`;
};
//# sourceMappingURL=money.js.map